import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { FileText, Loader2, CheckSquare, Square, Upload } from 'lucide-react';
import { toast } from 'sonner';

/** V66 — learner picks Medical-Vault documents to share INTO the open nutritionist chat. */
export const VaultPickerDialog = ({ open, onOpenChange, consultId, onShared }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/api/vault/files').then(r => setFiles((r.data.files || []).filter(f => f.category !== 'admin')))
      .catch(() => setFiles([])).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) { setPicked([]); load(); } }, [open]);

  const toggle = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Please keep documents under 5 MB.'); e.target.value = ''; return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/api/vault/upload?category=personal', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded to your Medical Vault');
      load();
      const id = r.data?.id;
      if (id) setPicked(p => [...p, id]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };

  const share = async () => {
    if (!picked.length) return;
    setBusy(true);
    try {
      const r = await api.post(`/api/medical/consult/${consultId}/attach`, { doc_ids: picked });
      toast.success(`Shared ${picked.length} document${picked.length > 1 ? 's' : ''} with your nutritionist`);
      onShared?.(r.data);
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detailObject || e.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Could not share');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="vault-picker-dialog">
        <DialogHeader>
          <DialogTitle>Share a document</DialogTitle>
          <DialogDescription>Pick reports from your Medical Vault. Your nutritionist can open them only while this chat is open.</DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto space-y-1.5" data-testid="vault-picker-list">
          {loading && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {!loading && files.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Your vault is empty — upload a report below.</p>}
          {files.map(f => {
            const on = picked.includes(f.id);
            return (
              <button key={f.id} type="button" onClick={() => toggle(f.id)} data-testid={`vault-pick-${f.id}`}
                className={`w-full flex items-center gap-2 p-2 rounded-lg border text-left text-sm transition-colors ${on ? 'border-emerald-400 bg-emerald-50' : 'hover:bg-muted/40'}`}>
                {on ? <CheckSquare className="h-4 w-4 text-emerald-600 shrink-0" /> : <Square className="h-4 w-4 text-muted-foreground shrink-0" />}
                <FileText className="h-4 w-4 text-sky-600 shrink-0" />
                <span className="truncate flex-1">{f.original_filename || f.file_name || 'Document'}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">{f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}</span>
              </button>
            );
          })}
        </div>
        <label className="flex items-center gap-2 text-xs text-emerald-700 cursor-pointer w-fit" data-testid="vault-picker-upload">
          <input type="file" className="hidden" onChange={upload} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" disabled={uploading} />
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Upload a new report
        </label>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={share} disabled={busy || !picked.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid="vault-picker-share-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Share ${picked.length || ''}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
